import { Bank } from '@core/model/domain/bank';
import { BANK_ALIASES } from './bank-aliases';

const MIN_TOKEN_PREFIX_LENGTH = 2;
const MIN_FUZZY_LENGTH = 4;
const MAX_FUZZY_DISTANCE = 2;

export function normalizeForSearch(input: string): string {
  return input
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function tokenize(input: string): string[] {
  return input.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
}

export function levenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a.length === 0) {
    return b.length;
  }
  if (b.length === 0) {
    return a.length;
  }

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  const current = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    current[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min((current[j - 1] ?? 0) + 1, (previous[j] ?? 0) + 1, (previous[j - 1] ?? 0) + cost);
    }
    previous = [...current];
  }

  return previous[b.length] ?? 0;
}

function matchesByContains(termNorm: string, candidateNorm: string): boolean {
  return candidateNorm.includes(termNorm) || termNorm.includes(candidateNorm);
}

function matchesByTokenPrefix(termNorm: string, candidateTokens: string[]): boolean {
  if (termNorm.length < MIN_TOKEN_PREFIX_LENGTH) {
    return false;
  }
  return candidateTokens.some((token) => token.startsWith(termNorm));
}

function matchesByFuzzy(termNorm: string, candidateNorm: string, candidateTokens: string[]): boolean {
  if (termNorm.length < MIN_FUZZY_LENGTH) {
    return false;
  }
  if (levenshtein(termNorm, candidateNorm) <= MAX_FUZZY_DISTANCE) {
    return true;
  }
  return candidateTokens.some(
    (token) => token.length >= MIN_FUZZY_LENGTH && levenshtein(termNorm, token) <= MAX_FUZZY_DISTANCE
  );
}

export function matchesBank(bank: Bank, term: string): boolean {
  const termNorm = normalizeForSearch(term);
  if (!termNorm) {
    return true;
  }

  const candidates = [bank.name, ...(BANK_ALIASES[bank.name] ?? [])];

  for (const candidate of candidates) {
    const candidateNorm = normalizeForSearch(candidate);
    const candidateTokens = tokenize(candidateNorm);

    if (matchesByContains(termNorm, candidateNorm)) {
      return true;
    }
    if (matchesByTokenPrefix(termNorm, candidateTokens)) {
      return true;
    }
    if (matchesByFuzzy(termNorm, candidateNorm, candidateTokens)) {
      return true;
    }
  }

  return false;
}

export function filterBanks(banks: Bank[], term: string): Bank[] {
  if (!normalizeForSearch(term)) {
    return banks;
  }
  return banks.filter((bank) => matchesBank(bank, term));
}
